import {FC, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import {getCertificateYearOptions} from '../../model/certificateYear';
import styles from './CertificateYearStep.module.css';

type Props = {
	value: number;
	onChange: (year: number) => void;
};

const CalendarIcon = () => (
	<svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none" aria-hidden>
		<path
			d="M7 3.5V6.5M17 3.5V6.5M4.5 9.5H19.5M6 5H18C19.1046 5 20 5.89543 20 7V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V7C4 5.89543 4.89543 5 6 5Z"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const ChevronIcon = ({open}: {open: boolean}) => (
	<svg
		className={cn(styles.chevronIcon, open && styles.chevronIconOpen)}
		viewBox="0 0 24 24"
		fill="none"
		aria-hidden
	>
		<path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const CheckIcon = () => (
	<svg className={styles.checkIcon} viewBox="0 0 24 24" fill="none" aria-hidden>
		<path d="M5 12.5L10 17.5L19 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const CertificateYearStep: FC<Props> = ({value, onChange}) => {
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const years = getCertificateYearOptions();

	useEffect(() => {
		if (!open) {
			return;
		}

		const handlePointerDown = (event: MouseEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handlePointerDown);

		return () => document.removeEventListener('mousedown', handlePointerDown);
	}, [open]);

	return (
		<div className={styles.root}>
			<h2 className={styles.title}>Выберите год</h2>

			<div className={styles.field} ref={rootRef}>
				<button
					type="button"
					className={styles.trigger}
					aria-expanded={open}
					aria-haspopup="listbox"
					onClick={() => setOpen((prev) => !prev)}
				>
					<span className={styles.triggerMain}>
						<CalendarIcon />
						<span className={styles.triggerValue}>{value}</span>
					</span>
					<ChevronIcon open={open} />
				</button>

				{open ? (
					<ul className={styles.list} role="listbox" aria-label="Год справки">
						{years.map((year) => {
							const selected = year === value;

							return (
								<li key={year}>
									<button
										type="button"
										role="option"
										aria-selected={selected}
										className={cn(styles.option, selected && styles.optionSelected)}
										onClick={() => {
											onChange(year);
											setOpen(false);
										}}
									>
										<span className={styles.optionLabel}>{year}</span>
										{selected ? <CheckIcon /> : null}
									</button>
								</li>
							);
						})}
					</ul>
				) : null}
			</div>
		</div>
	);
};
